/**
 * Shared theme token definitions.
 *
 * This is the single source of truth for all brandkit-overridable theme properties.
 * When adding a new theme color, add it to THEME_COLOR_TOKENS and the ThemeColorFields
 * interface. The resolver (`build-theme.ts`) and merge utilities pick it up automatically.
 */

import { CornerRadiusEnum } from "./corner-radius";

export interface ThemeColorFields {
  color?: string;
  colorPrimaryDarkMode?: string;
  colorBackground?: string;
  colorSurface?: string;
  colorSurfaceOpacity?: number;
  colorBorder?: string;
  colorBorderOpacity?: number;
  colorHeading?: string;
  colorText?: string;
  colorTextDarkMode?: string;
  colorButtonPrimary?: string;
  colorButtonPrimaryOpacity?: number;
  colorButtonText?: string;
  colorButtonTextOpacity?: number;
  feedbackColorPositive?: string;
  feedbackColorNegative?: string;
  colorProgress?: string;
}

export interface ThemeStyleFields {
  cornerRadius?: CornerRadiusEnum;
  /** When explicitly `false`, hides the pointer arrow on balloon callouts. */
  balloonArrowEnabled?: boolean;
}

export type ThemeOverrideFields = ThemeColorFields & ThemeStyleFields;

export type ThemeColorToken = {
  key: keyof ThemeColorFields;
  cssVar: string;
  resolvedVar: string;
  type: "color" | "opacity";
};

/**
 * All brandkit-overridable color tokens with their CSS variable mappings.
 *
 * To add a new color:
 * 1. Add the field to ThemeColorFields above
 * 2. Add an entry here
 * 3. Add the SCSS override variable in _shared.scss (inside both theme blocks)
 */
export const THEME_COLOR_TOKENS: ThemeColorToken[] = [
  {
    key: "color",
    cssVar: "--color-primary-override",
    resolvedVar: "--color-primary",
    type: "color",
  },
  {
    key: "colorPrimaryDarkMode",
    cssVar: "--color-primary-dark-override",
    resolvedVar: "--color-primary",
    type: "color",
  },
  {
    key: "colorBackground",
    cssVar: "--color-background-override",
    resolvedVar: "--color-background",
    type: "color",
  },
  {
    key: "colorSurface",
    cssVar: "--color-surface-override",
    resolvedVar: "--color-surface",
    type: "color",
  },
  {
    key: "colorSurfaceOpacity",
    cssVar: "--color-surface-opacity-override",
    resolvedVar: "--color-surface-opacity",
    type: "opacity",
  },
  {
    key: "colorBorder",
    cssVar: "--color-border-override",
    resolvedVar: "--color-border",
    type: "color",
  },
  {
    key: "colorBorderOpacity",
    cssVar: "--color-border-opacity-override",
    resolvedVar: "--color-border-opacity",
    type: "opacity",
  },
  {
    key: "colorText",
    cssVar: "--color-text-override",
    resolvedVar: "--color-text",
    type: "color",
  },
  {
    key: "colorTextDarkMode",
    cssVar: "--color-text-dark-override",
    resolvedVar: "--color-text",
    type: "color",
  },
  {
    key: "colorHeading",
    cssVar: "--color-heading-override",
    resolvedVar: "--color-heading",
    type: "color",
  },
  {
    key: "colorButtonPrimary",
    cssVar: "--color-button-primary-override",
    resolvedVar: "--color-button-primary",
    type: "color",
  },
  {
    key: "colorButtonPrimaryOpacity",
    cssVar: "--color-button-primary-opacity-override",
    resolvedVar: "--color-button-primary-opacity",
    type: "opacity",
  },
  {
    key: "colorButtonText",
    cssVar: "--color-button-text-override",
    resolvedVar: "--color-button-text",
    type: "color",
  },
  {
    key: "colorButtonTextOpacity",
    cssVar: "--color-button-text-opacity-override",
    resolvedVar: "--color-button-text-opacity",
    type: "opacity",
  },
  {
    key: "feedbackColorPositive",
    cssVar: "--color-feedback-positive-override",
    resolvedVar: "--color-feedback-positive",
    type: "color",
  },
  {
    key: "feedbackColorNegative",
    cssVar: "--color-feedback-negative-override",
    resolvedVar: "--color-feedback-negative",
    type: "color",
  },
  {
    key: "colorProgress",
    cssVar: "--color-progress-override",
    resolvedVar: "--color-progress",
    type: "color",
  },
];

export const THEME_COLOR_KEYS = THEME_COLOR_TOKENS.map((t) => t.key);

/**
 * Color keys that divisions are allowed to contribute to theming.
 */
export const DIVISION_THEME_COLOR_KEYS = new Set<keyof ThemeColorFields>([
  "color",
  "colorPrimaryDarkMode",
  "colorText",
  "colorTextDarkMode",
  "feedbackColorPositive",
  "feedbackColorNegative",
]);

/** Color tokens that divisions are allowed to set. */
export const DIVISION_THEME_COLOR_TOKENS = THEME_COLOR_TOKENS.filter((t) =>
  DIVISION_THEME_COLOR_KEYS.has(t.key)
);
export const THEME_STYLE_KEYS: (keyof ThemeStyleFields)[] = [
  "cornerRadius",
  "balloonArrowEnabled",
];
export const THEME_OVERRIDE_KEYS: (keyof ThemeOverrideFields)[] = [
  ...THEME_COLOR_KEYS,
  ...THEME_STYLE_KEYS,
];

/** Merge override fields onto a base. Override wins when set; opacity uses ?? to preserve 0. */
export function mergeThemeOverrides<T extends Partial<ThemeOverrideFields>>(
  base: T,
  override: Partial<ThemeOverrideFields>
): T {
  const result = { ...base };

  for (const token of THEME_COLOR_TOKENS) {
    const overrideValue = override[token.key];
    if (token.type === "opacity") {
      (result as any)[token.key] = overrideValue ?? (base as any)[token.key];
    } else {
      (result as any)[token.key] = overrideValue || (base as any)[token.key];
    }
  }

  for (const key of THEME_STYLE_KEYS) {
    const overrideValue = override[key];
    if (overrideValue != null) {
      (result as any)[key] = overrideValue;
    }
  }

  return result;
}

/**
 * A home page asking for its master's own colours instead of the project's
 * accent. Stored where a hex would be, because "no project accent here" has to
 * be tellable apart from "nothing set", which still inherits one.
 */
export const PAGE_ACCENT_NONE = "none";

/**
 * The accent a page renders under.
 *
 * Only the home page carries the project's colour — with the events view,
 * which has no page of its own and follows the home page's answer. Every other
 * page belongs to the master: its brandkit is the whole of its palette, so the
 * project's colour never reaches it and `undefined` here leaves the kit's own
 * accent standing through `mergeBrandkitOverrides` below.
 */
export function resolvePageAccent(
  pageColor: string | undefined,
  projectColor: string | undefined,
  options: { isHomePage: boolean }
): string | undefined {
  if (!options.isHomePage) return undefined;
  if (pageColor === PAGE_ACCENT_NONE) return undefined;
  return pageColor || projectColor;
}

export function mergeBrandkitOverrides<T extends Partial<ThemeOverrideFields>>(
  divisionContributions: T,
  brandkit: Partial<ThemeOverrideFields>,
  options?: { inherited?: boolean }
): T {
  const projectColor = divisionContributions.color;
  if (!options?.inherited || !projectColor)
    return mergeThemeOverrides(divisionContributions, brandkit);

  const accentless: Partial<ThemeOverrideFields> = {
    ...brandkit,
    color: undefined,
    colorButtonPrimary: undefined,
    colorButtonText: undefined,
  };

  return mergeThemeOverrides(divisionContributions, accentless);
}

/** Pick only theme override fields from a source object. */
export function pickThemeOverrides(
  source: Partial<ThemeOverrideFields>
): Partial<ThemeOverrideFields> {
  const result: Partial<ThemeOverrideFields> = {};

  for (const key of THEME_OVERRIDE_KEYS) {
    const value = (source as any)[key];
    if (value !== undefined) {
      (result as any)[key] = value;
    }
  }

  return result;
}

/**
 * Pick only the theme fields a division is allowed to contribute.
 * Strips fields like colorBackground, colorSurface, colorBorder, etc.
 * that should only come from brandkits.
 */
export function pickDivisionThemeOverrides(
  source: Partial<ThemeOverrideFields>
): Partial<ThemeOverrideFields> {
  const result: Partial<ThemeOverrideFields> = {};

  for (const key of THEME_COLOR_KEYS) {
    if (DIVISION_THEME_COLOR_KEYS.has(key as keyof ThemeColorFields)) {
      const value = (source as any)[key];
      if (value !== undefined) {
        (result as any)[key] = value;
      }
    }
  }

  for (const key of THEME_STYLE_KEYS) {
    const value = (source as any)[key];
    if (value !== undefined) {
      (result as any)[key] = value;
    }
  }

  return result;
}
